/**
 * Template de prise de besoin : Plan Directeur TI – PME
 * Version standard pour PME (20–250 employés)
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePdtiStandardPme: IntakeTemplate = {
  id: 'pdti-standard-pme',
  name: 'Prise de besoin Plan Directeur TI – PME',
  description:
    'Version standard pour PME (20–250 employés): contexte d\'affaires, maturité TI, enjeux, objectifs 3–5 ans, contraintes, priorités.',
  documentTypeCode: 'PLAN_DIRECTEUR',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Qualification Client',
      description: 'Informations générales sur l\'entreprise',
      icon: 'Building2',
      questions: [
        {
          id: 'company_name',
          label: "Nom de l'entreprise",
          type: 'text',
          required: true,
        },
        {
          id: 'company_type',
          label: "Type d'entreprise",
          type: 'select',
          options: ['Startup / Entreprise en démarrage', 'PME établie', 'Entreprise en croissance', 'Organisation'],
          required: true,
        },
        {
          id: 'industry',
          label: "Industrie / Secteur d'activité",
          type: 'select',
          options: [
            "Cabinet d'avocats / Juridique",
            'Cabinet comptable / CPA',
            'Services professionnels (Autre)',
            'Manufacturing / Production',
            'Commerce de détail',
            'Santé',
            'Finance / Assurance',
            'Technologies',
            'Construction',
            'Transport / Logistique',
            'Éducation',
            'Autre',
          ],
          required: true,
        },
        {
          id: 'employees',
          label: "Nombre d'employés",
          type: 'select',
          options: ['Moins de 25 employés', '25-49 employés', '50-100 employés', '100-250 employés', '250 employés et plus'],
          required: true,
        },
        {
          id: 'revenue',
          label: "Chiffre d'affaires annuel (approximatif)",
          type: 'select',
          options: ['Moins de 1M$', '1M$ - 3M$', '3M$ - 10M$', '10M$ - 25M$', '25M$ - 50M$', '50M$ et plus', 'À déterminer'],
          required: false,
        },
        {
          id: 'it_maturity',
          label: 'Maturité TI actuelle',
          type: 'select',
          options: [
            'Débutant - Infrastructure basique, peu de systèmes',
            'Intermédiaire - Systèmes en place, mais peu optimisés',
            'Avancé - Infrastructure solide, processus établis',
            'Expert - TI stratégique, innovation continue',
          ],
          required: true,
        },
        {
          id: 'it_team',
          label: 'Équipe TI actuelle',
          type: 'select',
          options: [
            'Aucune équipe dédiée',
            '1-2 personnes',
            '3-5 personnes',
            '6-10 personnes',
            '10+ personnes',
            'Totalement externalisé',
          ],
          required: true,
        },
        {
          id: 'budget_mensuel_bz',
          label: 'Budget mensuel estimé pour BZ (services TI)',
          type: 'select',
          options: [
            '1500$ - Généralement pour client petite taille (5-10 utilisateurs)',
            '2000$ - Généralement pour client moyenne taille (10-15 utilisateurs)',
            '3000$ - Généralement pour client moyenne taille (15-25 utilisateurs)',
            '4000$ - Généralement pour client moyenne-grande taille (25-40 utilisateurs)',
            '5000$ - Généralement pour client grande taille (40-60 utilisateurs)',
            '7500$ - Généralement pour client grande taille (60-100 utilisateurs)',
            '10000$+ - Généralement pour client très grande taille (100+ utilisateurs)',
            'À déterminer',
          ],
          required: false,
          helpText: 'Cette information nous permet de vous proposer des offres adaptées à votre budget. Nous présenterons 3 options : une offre optimisée, une offre recommandée (milieu), et une offre complète.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Contexte & Objectifs',
      description: 'Comprendre les défis et objectifs',
      icon: 'Target',
      questions: [
        {
          id: 'main_challenge',
          label: "Quel est votre principal défi TI actuellement ?",
          type: 'textarea',
          placeholder: "Ex: Notre infrastructure est obsolète et cause des interruptions fréquentes...",
          required: true,
        },
        {
          id: 'business_goals',
          label: "Objectifs d'affaires pour les 12 prochains mois",
          type: 'multiselect',
          options: [
            'Croissance des revenus',
            "Amélioration de l'efficacité opérationnelle",
            'Expansion géographique',
            'Nouveaux produits/services',
            'Transformation numérique',
            'Réduction des coûts',
            "Amélioration de l'expérience client",
            'Conformité réglementaire',
          ],
          required: true,
        },
        {
          id: 'it_goals',
          label: 'Quels sont vos objectifs TI prioritaires ?',
          type: 'multiselect',
          options: [
            "Moderniser l'infrastructure",
            "Améliorer la sécurité",
            'Migration cloud',
            'Automatisation des processus',
            'Business Intelligence / Analytics',
            'Mobilité des employés',
            'Collaboration à distance',
            'Innovation / IA',
            'Réduire les temps d\'arrêt',
            'Optimiser les coûts TI',
          ],
          required: true,
        },
        {
          id: 'timeline',
          label: "Quel est votre horizon temporel ?",
          type: 'select',
          options: ['Urgent (0-3 mois)', 'Court terme (3-6 mois)', 'Moyen terme (6-12 mois)', 'Long terme (1-2 ans)', 'Stratégique (2-5 ans)'],
          required: true,
        },
        {
          id: 'budget_range',
          label: "Budget estimé pour ce projet",
          type: 'select',
          options: ['Moins de 25K$', '25K$ - 50K$', '50K$ - 100K$', '100K$ - 250K$', '250K$ - 500K$', '500K$ et plus', 'À déterminer'],
          required: false,
        },
        {
          id: 'success_metrics',
          label: "Comment mesurerez-vous le succès de ce projet ?",
          type: 'textarea',
          placeholder: "Ex: Réduction de 50% des temps d'arrêt, augmentation de 30% de la productivité...",
          required: true,
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Besoins Techniques',
      description: 'Infrastructure et systèmes actuels',
      icon: 'Server',
      questions: [
        {
          id: 'current_systems',
          label: 'Systèmes critiques actuels',
          type: 'multiselect',
          options: [
            'ERP (SAP, Oracle, Dynamics, etc.)',
            'CRM (Salesforce, HubSpot, etc.)',
            'Comptabilité / Finance',
            'Gestion de projet',
            'RH / Paie',
            'E-commerce',
            'Inventory / Supply Chain',
            'Manufacturing Execution System (MES)',
            'Business Intelligence / Analytics',
            'Autre',
          ],
          required: true,
        },
        {
          id: 'infrastructure',
          label: 'Infrastructure actuelle',
          type: 'multiselect',
          options: [
            'Serveurs on-premise',
            'Cloud public (AWS, Azure, GCP)',
            'Cloud privé',
            'Hybride (on-premise + cloud)',
            'Hébergement tiers',
            'SaaS uniquement',
          ],
          required: true,
        },
        {
          id: 'pain_points',
          label: 'Points de douleur principaux',
          type: 'multiselect',
          options: [
            'Performance lente',
            'Pannes fréquentes',
            'Sécurité insuffisante',
            'Manque d\'intégration entre systèmes',
            'Processus manuels trop nombreux',
            'Difficulté à obtenir des rapports',
            'Support utilisateur insuffisant',
            'Coûts TI trop élevés',
            'Technologie obsolète',
            'Manque de visibilité sur les opérations',
          ],
          required: true,
        },
        {
          id: 'priorities',
          label: 'Classez vos priorités (sélectionnez top 3)',
          type: 'multiselect',
          options: [
            'Sécurité & Conformité',
            'Performance & Disponibilité',
            'Innovation & Compétitivité',
            'Réduction des coûts',
            'Expérience utilisateur',
            'Scalabilité / Croissance',
            'Agilité & Flexibilité',
          ],
          required: true,
          maxSelections: 3,
        },
        {
          id: 'security_concerns',
          label: 'Préoccupations de sécurité',
          type: 'multiselect',
          options: [
            'Protection des données clients',
            'Conformité réglementaire (RGPD, HIPAA, etc.)',
            'Cyberattaques / Ransomware',
            'Accès non autorisés',
            'Perte de données',
            'Backup & Disaster Recovery',
            'Formation des employés à la sécurité',
            'Aucune préoccupation majeure',
          ],
          required: true,
        },
        {
          id: 'integration_needs',
          label: 'Besoins d\'intégration',
          type: 'textarea',
          placeholder: 'Ex: Intégration entre ERP et comptabilité, automatisation des rapports...',
          required: false,
        },
        {
          id: 'users_impacted',
          label: "Nombre d'utilisateurs impactés par ce projet",
          type: 'select',
          options: ['1-10 utilisateurs', '11-25 utilisateurs', '26-50 utilisateurs', '51-100 utilisateurs', '100+ utilisateurs', 'Toute l\'entreprise'],
          required: true,
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Gouvernance & Support',
      description: 'Structure décisionnelle et modèle de support',
      icon: 'FileText',
      questions: [
        {
          id: 'decision_makers',
          label: "Qui sont les décideurs clés pour ce projet ?",
          type: 'textarea',
          placeholder: 'Ex: DG, Directeur des opérations, CFO...',
          required: true,
        },
        {
          id: 'stakeholders',
          label: 'Parties prenantes principales',
          type: 'multiselect',
          options: [
            'Direction générale (C-Suite)',
            'Finances / Comptabilité',
            'Opérations / Production',
            'Ventes / Marketing',
            'RH',
            'Service client',
            'Tous les départements',
          ],
          required: true,
        },
        {
          id: 'change_readiness',
          label: "L'organisation est-elle prête au changement ?",
          type: 'select',
          options: [
            'Très réticente - beaucoup de résistance attendue',
            'Modérément réticente - un peu de résistance',
            'Neutre - acceptation passive',
            'Ouverte - bonne volonté générale',
            'Très enthousiaste - demande active de changement',
          ],
          required: true,
        },
        {
          id: 'training_needs',
          label: 'Besoins de formation',
          type: 'select',
          options: [
            'Formation minimale - utilisateurs autonomes',
            'Formation de base - quelques sessions',
            'Formation complète - programme structuré',
            'Formation continue - support à long terme',
          ],
          required: true,
        },
        {
          id: 'support_model',
          label: 'Modèle de support souhaité',
          type: 'select',
          options: [
            'Support réactif - intervention sur demande',
            'Support proactif - surveillance et prévention',
            'Support dédié - ressource à temps plein',
            'Support hybride - mix des approches',
            'Support interne - notre équipe gère',
          ],
          required: true,
        },
        {
          id: 'additional_info',
          label: 'Informations additionnelles ou préoccupations',
          type: 'textarea',
          placeholder: 'Tout ce que nous devrions savoir d\'autre...',
          required: false,
        },
      ],
    },
  ],
};






